import { ConnectedLead, LeadConnectionSummary } from "./campaignConnections.api";

/**
 * What the assign pickers say about leads already connected on LinkedIn (PREC-4426, the confirmed
 * mockup): an amber warning for a connection to the campaign's sender, which the invite step stops
 * for (or skips, when its switch is off); a grey hint for one lead connected only to a colleague,
 * whose connection does not block the sender's invite. Inline and non-blocking: the user can still
 * add the lead.
 */
export type AlreadyConnectedNotice =
	| { kind: "none" }
	| { kind: "senderSingle"; lead: ConnectedLead; stops: boolean }
	| { kind: "senderBulk"; count: number; total: number; leads: ConnectedLead[]; stops: boolean }
	| { kind: "colleague"; lead: ConnectedLead };

export const NO_NOTICE: AlreadyConnectedNotice = { kind: "none" };

export function deriveAlreadyConnectedNotice(
	summary: LeadConnectionSummary | undefined,
	{ bulk, stops }: { bulk: boolean; stops: boolean }
): AlreadyConnectedNotice {
	if (!summary) {
		return NO_NOTICE;
	}

	const { senderConnected, colleagueOnly } = summary;

	if (senderConnected.count > 0) {
		return bulk
			? {
					kind: "senderBulk",
					count: senderConnected.count,
					total: summary.total,
					leads: senderConnected.leads,
					stops,
				}
			: { kind: "senderSingle", lead: senderConnected.leads[0], stops };
	}

	return !bulk && colleagueOnly.count > 0
		? { kind: "colleague", lead: colleagueOnly.leads[0] }
		: NO_NOTICE;
}

/** How many leads the button adds: the selection, less the connected ones when they are left out. */
export const leadsToAdd = (
	selectionCount: number,
	notice: AlreadyConnectedNotice,
	skipConnected: boolean
): number =>
	notice.kind === "senderBulk" && skipConnected
		? Math.max(0, notice.total - notice.count)
		: selectionCount;
